import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useObservable } from '@/shared/hooks/useObservable';
import { notificationService } from '../services/notification.service';
import { mapService } from '@/features/map/services/map.service';
import type { ZoneSettings } from '@/features/map/services/map.service';
import { Input } from '@/shared/components/Input';
import { flightCategoriesService } from '../services/flightCategories.service';
import { authService } from '@/features/auth/services/auth.service';
import { farthestFlightAreaDistance } from '@/features/map/utils/zone-calculator';

function FieldLabel({ sl, en }: { sl: string; en: string }) {
  return (
    <label className="text-xs">
      <span className="text-slate-300 font-medium">{sl}</span>
      <span className="text-slate-500"> / {en}</span>
    </label>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{children}</div>;
}

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  color: string;
  prefix?: string;
  onChange: (v: number) => void;
}

function SliderRow({ label, value, min, max, step = 1, color, prefix, onChange }: SliderRowProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center text-xs">
        <span className="text-slate-400">{label}</span>
        <span className={`font-mono font-medium ${color}`}>{prefix}{value} m</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-slate-700 accent-current"
        style={{ accentColor: color.includes('yellow') ? '#eab308' : '#ef4444' }}
      />
      <div className="flex justify-between text-[10px] text-slate-600">
        <span>{min}m</span>
        <span>{max}m</span>
      </div>
    </div>
  );
}

export function StepMap() {
  const data = useObservable(notificationService.formData$, notificationService.formData$.value);
  const geometry = useObservable(mapService.geometry$, mapService.geometry$.value);
  const zoneSettings = useObservable(mapService.zoneSettings$, mapService.zoneSettings$.value);
  const activeDrawingTool = useObservable(mapService.activeDrawingTool$, mapService.activeDrawingTool$.value);
  const polygonError = useObservable(mapService.polygonError$, mapService.polygonError$.value);

  const hasFlightArea = geometry.flightArea.length >= 3;
  const isPickingMarker = activeDrawingTool === 'marker';
  const isPickingPolygon = activeDrawingTool === 'polygon';

  const categories = useObservable(flightCategoriesService.categories$, flightCategoriesService.categories$.value);
  const category = categories.find(c => c.id === data.category);
  const user = useObservable(authService.user$, null);
  const selectedUas = user?.uasList.find(u => u.id === data.uasId);

  const maxAltitude = category?.maxAltitudeM ?? 120;
  const minTermination = category?.terminationRatioAltitude
    ? Math.ceil(data.plannedAltitude * category.terminationRatioAltitude)
    : null;
  const minBuffer = category?.bufferAltitudeRatio
    ? Math.ceil(data.plannedAltitude * category.bufferAltitudeRatio)
    : null;
  const bufferInvalid = !!category?.requiresBufferZone && (
    zoneSettings.bufferMeters <= 0 || (minBuffer != null && zoneSettings.bufferMeters < minBuffer)
  );
  const terminationInvalid = !!category?.requiresTerminationZone && (
    zoneSettings.terminationMeters <= 0 || (minTermination != null && zoneSettings.terminationMeters < minTermination)
  );

  useEffect(() => {
    if (!category) return;
    const patch: Partial<ZoneSettings> = {};
    if (!category.requiresBufferZone && zoneSettings.bufferMeters !== 0) {
      patch.bufferMeters = 0;
    }
    if (!category.requiresTerminationZone && zoneSettings.terminationMeters !== 0) {
      patch.terminationMeters = 0;
    }
    if (Object.keys(patch).length > 0) {
      mapService.setZoneSettings(patch);
    }
  }, [category?.id, category?.requiresBufferZone, category?.requiresTerminationZone]);

  useEffect(() => {
    if (category?.maxAltitudeM != null && data.plannedAltitude > category.maxAltitudeM) {
      notificationService.updateFormData({ plannedAltitude: category.maxAltitudeM });
    }
  }, [category?.id, category?.maxAltitudeM]);

  const allTakeoffPoints = geometry.takeoffPoint
    ? [geometry.takeoffPoint, ...geometry.additionalTakeoffPoints]
    : geometry.additionalTakeoffPoints;
  const farthestDistance = hasFlightArea ? farthestFlightAreaDistance(geometry.flightArea, allTakeoffPoints) : 0;
  const vlosWarning = selectedUas?.vlosMaxDistanceM != null && farthestDistance > selectedUas.vlosMaxDistanceM;
  const mtomWarning = !!category && selectedUas?.mtomKg != null && (
    (category.minMtomKg != null && selectedUas.mtomKg < category.minMtomKg) ||
    (category.maxMtomKg != null && selectedUas.mtomKg > category.maxMtomKg)
  );

  return (
    <div className="space-y-4">
      <SectionTitle>Risanje na karti / Draw on map</SectionTitle>

      {vlosWarning && (
        <p className="text-xs text-amber-400 bg-amber-950/30 border border-amber-800 rounded-md p-2">
          Področje letenja sega {farthestDistance.toFixed(0)} m od najbližje točke vzleta, kar presega VLOS doseg naprave ({selectedUas?.vlosMaxDistanceM} m).
          <br />
          Flight area extends {farthestDistance.toFixed(0)} m from the nearest takeoff point, beyond this device's VLOS range ({selectedUas?.vlosMaxDistanceM} m).
        </p>
      )}
      {mtomWarning && (
        <p className="text-xs text-amber-400 bg-amber-950/30 border border-amber-800 rounded-md p-2">
          MTOM naprave ({selectedUas?.mtomKg} kg) ne ustreza izbrani kategoriji / Device MTOM ({selectedUas?.mtomKg} kg) does not match the selected category.
        </p>
      )}

      <div className="bg-slate-900 border border-slate-700 rounded-md p-3 text-xs text-slate-500 leading-relaxed">
        <>
          <span className="text-slate-300">1.</span> Kliknite za točko vzleta / Click for takeoff point
          <br />
          <span className="text-slate-300">2.</span> Narišite področje letenja / Draw the flight area polygon
        </>
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Točka vzleta" en="Takeoff point" />
        <Input
          prefilled={!!geometry.takeoffPoint}
          readOnly
          onClick={() => mapService.setActiveDrawingTool('marker')}
          className={isPickingMarker ? 'border-amber-500 ring-1 ring-amber-500 cursor-crosshair' : 'cursor-pointer'}
          value={
            geometry.takeoffPoint
              ? `${geometry.takeoffPoint.lat.toFixed(5)}°N, ${geometry.takeoffPoint.lng.toFixed(5)}°E`
              : ''
          }
          placeholder={isPickingMarker ? 'Kliknite na karto... / Click map...' : 'Kliknite na karto / Click map'}
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <FieldLabel sl="Dodatne točke vzleta" en="Additional takeoff points" />
          <button
            type="button"
            onClick={() => mapService.setActiveDrawingTool('additional-marker')}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            + Dodaj / Add
          </button>
        </div>
        {geometry.additionalTakeoffPoints.length > 0 && (
          <ul className="space-y-1">
            {geometry.additionalTakeoffPoints.map((p, i) => (
              <li
                key={i}
                className="flex items-center justify-between gap-2 bg-slate-900 border border-slate-700 rounded-md px-2 py-1"
              >
                <span className="text-xs text-slate-400 font-mono">
                  {p.lat.toFixed(5)}°N, {p.lng.toFixed(5)}°E
                </span>
                <button
                  type="button"
                  onClick={() => mapService.setAdditionalTakeoffPoints(
                    geometry.additionalTakeoffPoints.filter((_, idx) => idx !== i)
                  )}
                  className="text-rose-400 hover:text-rose-300 text-xs leading-none"
                  title="Odstrani / Remove"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Načrtovana višina letenja" en="Planned altitude" />
        <div className="flex gap-2 items-end">
          <Input
            type="number"
            min={1}
            max={maxAltitude}
            value={data.plannedAltitude}
            onChange={e => notificationService.updateFormData({ plannedAltitude: Number(e.target.value) })}
            className="text-right"
          />
          <span className="text-xs text-violet-400 whitespace-nowrap mb-2">max {maxAltitude}m AGL</span>
        </div>
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Največja načrtovana oddaljenost" en="Max planned distance" />
        <div className="flex gap-2 items-end">
          <Input
            type="number"
            min={1}
            value={data.maxDistance ?? ''}
            onChange={e => notificationService.updateFormData({ maxDistance: Number(e.target.value) })}
            className="text-right"
          />
          <span className="text-xs text-slate-500 whitespace-nowrap mb-2">m (VLOS)</span>
        </div>
      </div>

      <div className="space-y-1">
        <FieldLabel sl="Področje letenja" en="Flight area" />
        <Input
          prefilled={hasFlightArea}
          readOnly
          onClick={() => {
            mapService.setActiveDrawingTool('polygon');
            mapService.setPolygonError(null);
          }}
          className={isPickingPolygon ? 'border-amber-500 ring-1 ring-amber-500 cursor-crosshair' : 'cursor-pointer'}
          value={hasFlightArea ? `${geometry.flightArea.length} točk / points` : ''}
          placeholder={isPickingPolygon ? 'Narišite poligon... / Draw polygon...' : 'Narišite poligon / Draw polygon'}
        />
        {polygonError && (
          <p className="text-xs text-red-400">{polygonError}</p>
        )}
      </div>

      {hasFlightArea && (category?.requiresBufferZone || category?.requiresTerminationZone) && (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 space-y-4">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
            Cone / Zones
          </div>
          {category?.requiresBufferZone && (
            <>
              <SliderRow
                label="Blažilec / Buffer"
                value={zoneSettings.bufferMeters}
                min={0}
                max={1000}
                color="text-yellow-400"
                onChange={v => mapService.setZoneSettings({ bufferMeters: v })}
              />
              {minBuffer != null && (
                <p className="text-[10px] text-slate-600">
                  min {minBuffer} m pri {data.plannedAltitude} m višine (razmerje {category.bufferAltitudeRatio})
                </p>
              )}
              {bufferInvalid && (
                <p className="text-xs text-amber-400">
                  {minBuffer != null
                    ? `Blažilec mora biti vsaj ${minBuffer} m / Buffer must be at least ${minBuffer} m`
                    : 'Blažilec mora biti večji od 0 m / Buffer must be greater than 0 m'}
                </p>
              )}
            </>
          )}
          {category?.requiresTerminationZone && (
            <>
              <SliderRow
                label="Terminacija / Termination"
                value={zoneSettings.terminationMeters}
                min={0}
                max={1000}
                color="text-red-400"
                onChange={v => mapService.setZoneSettings({ terminationMeters: v })}
              />
              {minTermination != null && (
                <p className="text-[10px] text-slate-600">
                  min {minTermination} m pri {data.plannedAltitude} m višine (razmerje {category.terminationRatioAltitude})
                </p>
              )}
              {terminationInvalid && (
                <p className="text-xs text-amber-400">
                  {minTermination != null
                    ? `Terminacija mora biti vsaj ${minTermination} m / Termination must be at least ${minTermination} m`
                    : 'Terminacija mora biti večja od 0 m / Termination must be greater than 0 m'}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
